import React, { useMemo } from "react";
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  useMediaQuery,
  useTheme,
  Tooltip,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { useSelector } from "react-redux";

import { IStoreState } from "../../../../redux/initialStoreState";
import {
  IFoodMenuCategory,
  ISubCategoriesJson,
} from "../../../../redux/food/food.types";
import { useMenuBuilder } from "../context/MenuBuilderContext";
import { filterCategories } from "../utils";

// ─── Light Theme Colors ─────────────────────────────────────────────────────────

const lightColors = {
  panelBg: "#FFFFFF",
  panelBorder: "#E2E8F0",
  textPrimary: "#1E293B",
  textSecondary: "#64748B",
  textMuted: "#94A3B8",
  activeRowBg: "#E8F0F8",
  activeRowBorder: "#0D3B66",
  activeText: "#0D3B66",
  hoverBg: "#F8FAFC",
  badgeBg: "#F1F5F9",
  badgeActiveBg: "#0D3B66",
  badgeActiveText: "#FFFFFF",
  searchBg: "#F1F5F9",
  searchBorder: "#E2E8F0",
  searchFocusBorder: "#0D3B66",
  divider: "#E2E8F0",
};

// ─── Category Row ───────────────────────────────────────────────────────────────

interface ICategoryRowProps {
  category: IFoodMenuCategory;
}

const CategoryRow: React.FC<ICategoryRowProps> = ({ category }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { activeCategory, selectCategory, setActiveTab, mode, isItemVisible } = useMenuBuilder();

  const categoryUuid = category.category_uuid ?? "";
  const isActive = activeCategory?.category_uuid === categoryUuid;

  const totalItems = category.food_items?.length || 0;
  const subcategoryItemCount = category.subcategories
    ? category.subcategories.reduce(
        (sum, sub) => sum + (sub.food_items?.length || 0),
        0
      )
    : 0;
  const allItemCount = totalItems + subcategoryItemCount;

  // In lead mode, only count visible items
  const totalItemCount = mode === "lead"
    ? (() => {
        let count = 0;
        if (category.food_items) {
          count += category.food_items.filter((item) => isItemVisible(item.food_item_uuid ?? "")).length;
        }
        if (category.subcategories) {
          for (const sub of category.subcategories) {
            if (sub.food_items) {
              count += sub.food_items.filter((item) => isItemVisible(item.food_item_uuid ?? "")).length;
            }
          }
        }
        return count;
      })()
    : allItemCount;

  const handleCategoryClick = () => {
    if (isActive) {
      selectCategory(null);
    } else {
      selectCategory(category);
      if (isMobile) {
        setActiveTab("items");
      }
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        cursor: "pointer",
        py: 1,
        px: 1.5,
        borderRadius: "10px",
        borderLeft: isActive ? `3px solid ${lightColors.activeRowBorder}` : "3px solid transparent",
        backgroundColor: isActive ? lightColors.activeRowBg : "transparent",
        transition: "all 0.15s ease",
        "&:hover": {
          backgroundColor: isActive ? lightColors.activeRowBg : lightColors.hoverBg,
        },
      }}
      onClick={handleCategoryClick}
    >
      <Typography
        sx={{
          flex: 1,
          fontWeight: isActive ? 700 : 500,
          color: isActive ? lightColors.activeText : lightColors.textPrimary,
          fontSize: "14px",
        }}
      >
        {category.category_name}
      </Typography>

      {/* Count badge */}
      <Box
        sx={{
          minWidth: 26,
          height: 26,
          borderRadius: "13px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          px: 0.5,
          backgroundColor: isActive ? lightColors.badgeActiveBg : lightColors.badgeBg,
        }}
      >
        <Typography
          sx={{
            fontSize: "12px",
            fontWeight: 700,
            color: isActive ? lightColors.badgeActiveText : lightColors.textSecondary,
          }}
        >
          {totalItemCount}
        </Typography>
      </Box>
    </Box>
  );
};

// ─── CategoryPanel ──────────────────────────────────────────────────────────────

export const CategoryPanel: React.FC = () => {
  const { categorySearchText, setCategorySearchText, mode, visibilityEditMode, toggleVisibilityEditMode, isItemVisible } = useMenuBuilder();

  const categories = useSelector(
    (state: IStoreState) => state.food.foodCategory.data
  );

  const filteredCategories = useMemo(
    () => filterCategories(categories, categorySearchText),
    [categories, categorySearchText]
  );

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        backgroundColor: lightColors.panelBg,
        borderRight: `1px solid ${lightColors.panelBorder}`,
        overflow: "hidden",
      }}
    >
      {/* CATEGORIES Label + Fixed Toggle/Chip */}
      <Box sx={{ px: 1.5, pt: 1.5, pb: 0.5, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Typography
          sx={{
            fontSize: "11px",
            fontWeight: 700,
            letterSpacing: "0.05em",
            textTransform: "uppercase",
            color: lightColors.textMuted,
          }}
        >
          CATEGORIES
        </Typography>
        {mode === "master" && (
          <Tooltip title={visibilityEditMode ? "Master menu is Fixed" : "Make Master menu Fixed"}>
            <Box
              onClick={toggleVisibilityEditMode}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                cursor: "pointer",
                bgcolor: visibilityEditMode ? "#E8F0F8" : lightColors.badgeBg,
                borderRadius: "12px",
                px: 1,
                py: 0.25,
                border: visibilityEditMode ? `1px solid ${lightColors.activeRowBorder}` : `1px solid ${lightColors.divider}`,
                transition: "all 0.2s",
              }}
            >
              <Box
                sx={{
                  width: 24,
                  height: 14,
                  borderRadius: "7px",
                  bgcolor: visibilityEditMode ? lightColors.activeRowBorder : "#CBD5E1",
                  position: "relative",
                  transition: "all 0.2s",
                }}
              >
                <Box
                  sx={{
                    width: 10,
                    height: 10,
                    borderRadius: "50%",
                    bgcolor: "#fff",
                    position: "absolute",
                    top: 2,
                    left: visibilityEditMode ? 12 : 2,
                    transition: "left 0.2s",
                  }}
                />
              </Box>
              <Typography sx={{ fontSize: "10px", fontWeight: 600, color: visibilityEditMode ? lightColors.activeRowBorder : lightColors.textMuted }}>
                Fixed
              </Typography>
            </Box>
          </Tooltip>
        )}
        {mode === "lead" && visibilityEditMode && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              bgcolor: "#E8F0F8",
              borderRadius: "12px",
              px: 1,
              py: 0.25,
              border: `1px solid ${lightColors.activeRowBorder}`,
            }}
          >
            <Typography sx={{ fontSize: "10px", fontWeight: 700, color: lightColors.activeRowBorder, letterSpacing: "0.03em" }}>
              FIXED MENU
            </Typography>
          </Box>
        )}
      </Box>

      {/* Search Input */}
      <Box sx={{ px: 1.5, pt: 0.5, pb: 1 }}>
        <TextField
          fullWidth
          size="small"
          placeholder="Search Category"
          value={categorySearchText}
          onChange={(e) => setCategorySearchText(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: lightColors.textMuted, fontSize: 18 }} />
              </InputAdornment>
            ),
          }}
          sx={{
            "& .MuiOutlinedInput-root": {
              backgroundColor: lightColors.searchBg,
              borderRadius: "10px",
              color: lightColors.textPrimary,
              fontSize: "13px",
              "& fieldset": {
                borderColor: lightColors.searchBorder,
              },
              "&:hover fieldset": {
                borderColor: "#CBD5E1",
              },
              "&.Mui-focused fieldset": {
                borderColor: lightColors.searchFocusBorder,
              },
            },
            "& .MuiInputBase-input::placeholder": {
              color: lightColors.textMuted,
              opacity: 1,
            },
          }}
        />
      </Box>

      {/* Scrollable Category List */}
      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          display: "flex",
          flexDirection: "column",
          gap: 0.25,
          px: 0.75,
          pb: 1,
          "&::-webkit-scrollbar": {
            width: "5px",
          },
          "&::-webkit-scrollbar-track": {
            background: "transparent",
          },
          "&::-webkit-scrollbar-thumb": {
            background: "#CBD5E1",
            borderRadius: "3px",
          },
          "&::-webkit-scrollbar-thumb:hover": {
            background: "#94A3B8",
          },
          scrollbarWidth: "thin",
          scrollbarColor: "#CBD5E1 transparent",
        }}
      >
        {filteredCategories
          .filter((category) => {
            // In lead mode, hide categories with 0 visible items
            if (mode === "lead") {
              let visibleCount = 0;
              if (category.food_items) {
                visibleCount += category.food_items.filter((item) => isItemVisible(item.food_item_uuid ?? "")).length;
              }
              if (category.subcategories) {
                for (const sub of category.subcategories) {
                  if (sub.food_items) {
                    visibleCount += sub.food_items.filter((item) => isItemVisible(item.food_item_uuid ?? "")).length;
                  }
                }
              }
              return visibleCount > 0;
            }
            return true;
          })
          .map((category) => (
          <CategoryRow key={category.category_uuid} category={category} />
        ))}

        {filteredCategories.length === 0 && (
          <Box sx={{ textAlign: "center", py: 4 }}>
            <Typography variant="body2" sx={{ color: lightColors.textMuted }}>
              No categories found
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};
